import { BannerTypeEnum } from '@api/enum/announcement.enum';
import { IClient } from '@api/models';
import { saveAnnouncementBanner } from '@api/services/announcement.service';
import useDialog from '@hooks/use-dialog';
import { Button, HelpText } from '@hyperflake/react-ui-library';
import { Label } from '@library/forms';
import { ErrorMessage, Formik, FormikHelpers } from 'formik';
import { FormikRadio, FormikSelect, FormikSwitch } from 'modules/shared/features/formik';
import FormikTextarea from 'modules/shared/features/formik/components/FormikTextarea/FormikTextarea';
import { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';
import * as Yup from 'yup';
import AnnouncementConfirmationDrawer from '../AnnouncementConfirmationDrawer/AnnouncementConfirmationDrawer';

const announcementBannerSchema = Yup.object().shape({
    message: Yup.string().required('Message is required'),
    type: Yup.string().oneOf(Object.values(BannerTypeEnum)).required('Type is required'),
    isActive: Yup.boolean().required(),
});

interface IAnnouncementBanner {
    _id?: string;
    message: string;
    type: BannerTypeEnum;
    isActive: boolean;
    clients: string[];
}

interface AnnouncementFormProps {
    announcement?: IAnnouncementBanner;
    selectedIds: string[];
    clients: IClient[];
    resetState: () => void;
    isLoading?: boolean;
}

type AnnouncementBannerFormValues = {
    message: string;
    type: BannerTypeEnum;
    isActive: boolean;
};

const AnnouncementCreateForm = ({
    announcement,
    selectedIds,
    clients,
    resetState,
    isLoading,
}: AnnouncementFormProps) => {
    const confirmationDrawerDialog = useDialog<IClient[]>();
    const [formValues, setFormValues] = useState<AnnouncementBannerFormValues | null>(null);
    const [isSending, setIsSending] = useState(false);
    const resetFormRef = useRef<(() => void) | null>(null);

    const initialValues: AnnouncementBannerFormValues = {
        message: announcement?.message || '',
        type: announcement?.type || BannerTypeEnum.INFO,
        isActive: announcement?.isActive ?? false,
    };

    const handleSubmit = (
        values: AnnouncementBannerFormValues,
        { resetForm }: FormikHelpers<AnnouncementBannerFormValues>
    ) => {
        resetFormRef.current = resetForm;
        setFormValues(values);

        confirmationDrawerDialog.show(clients.filter((client) => selectedIds.includes(client._id)));
    };

    const handleSaveAnnouncement = async () => {
        if (!formValues) return;

        try {
            setIsSending(true);

            await saveAnnouncementBanner({
                message: formValues.message,
                type: formValues.type,
                isActive: formValues.isActive,
                clients: selectedIds,
            });

            toast.success(announcement?._id ? 'Announcement banner updated' : 'Announcement banner created');
            confirmationDrawerDialog.hide();
            resetFormRef.current?.();
            resetState();
        } catch (err: any) {
            console.error(err);
            toast.error(err?.data?.message || 'Something went wrong.');
        } finally {
            setIsSending(false);
        }
    };

    if (isLoading) {
        return <p className="text-sm text-grayscale-500">Loading announcement...</p>;
    }

    const getBannerTypeClasses = (type: BannerTypeEnum) => {
        let bgClassWithTransparency = '';
        let textClass = '';

        switch (type) {
            case BannerTypeEnum.SUCCESS:
                bgClassWithTransparency = 'bg-success-100';
                textClass = 'text-success-500';
                break;
            case BannerTypeEnum.WARNING:
                bgClassWithTransparency = 'bg-warning-100';
                textClass = 'text-warning-500';
                break;
            case BannerTypeEnum.ERROR:
                bgClassWithTransparency = 'bg-danger-100';
                textClass = 'text-danger-500';
                break;
            case BannerTypeEnum.INFO:
                bgClassWithTransparency = 'bg-grayscale-100';
                textClass = 'text-grayscale-500';
                break;
        }

        return { bgClassWithTransparency, textClass };
    };

    return (
        <>
            <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={announcementBannerSchema}
                onSubmit={handleSubmit}
            >
                {({ handleSubmit, values, setFieldValue }) => (
                    <form onSubmit={handleSubmit}>
                        <h2 className="font-semibold text-base">
                            {announcement?._id ? 'Update Announcement Banner' : 'Create Announcement Banner'}
                        </h2>

                        <label htmlFor="isActive" className="block mt-4">
                            <div
                                className={twMerge(
                                    'flex justify-between items-center border border-grayscale-100 rounded-lg px-4 py-3',
                                    values.isActive ? 'text-primary border-primary bg-primary/5' : ''
                                )}
                            >
                                <Label className="mb-0">Is Active</Label>

                                <div className="mt-2">
                                    <FormikSwitch name="isActive" />
                                </div>
                            </div>

                            <HelpText variant="error">
                                <ErrorMessage name="isActive" />
                            </HelpText>
                        </label>

                        <div className="mt-4">
                            <Label>Banner Type</Label>

                            <div className="grid grid-cols-4 gap-1 mt-2">
                                {Object.values(BannerTypeEnum).map((type) => {
                                    const isSelected = values.type === type;
                                    const { bgClassWithTransparency, textClass } = getBannerTypeClasses(type);

                                    return (
                                        <label
                                            key={type}
                                            className={twMerge(
                                                'flex items-center justify-center text-center gap-3 cursor-pointer rounded-md border border-transparent px-4 py-2 transition-colors',
                                                bgClassWithTransparency,
                                                textClass,
                                                isSelected && 'border-primary ring-1 ring-primary'
                                            )}
                                        >
                                            <FormikRadio
                                                type="radio"
                                                name="type"
                                                value={type}
                                                checked={isSelected}
                                                onChange={() => setFieldValue('type', type)}
                                                className="accent-current"
                                                hidden
                                            />

                                            <span className="text-xs text-center font-medium">{type}</span>
                                        </label>
                                    );
                                })}
                            </div>

                            <HelpText variant="error">
                                <ErrorMessage name="type" />
                            </HelpText>
                        </div>

                        <div className="mt-4">
                            <Label>Message</Label>
                            <FormikTextarea rows={4} name="message" placeholder="Enter announcement message" />
                            <HelpText variant="error">
                                <ErrorMessage name="message" />
                            </HelpText>
                        </div>

                        <div className="mt-4">
                            <Button type="submit" color="primary" disabled={selectedIds.length === 0 || isSending}>
                                Save
                            </Button>
                        </div>
                    </form>
                )}
            </Formik>

            <AnnouncementConfirmationDrawer
                open={confirmationDrawerDialog.isOpen}
                onClose={confirmationDrawerDialog.hide}
                selectedClient={confirmationDrawerDialog.data}
                isSending={isSending}
                onSend={handleSaveAnnouncement}
                title={announcement?._id ? 'Update Announcement Banner' : 'Create Announcement Banner'}
                text={formValues?.message ?? ''}
                type={formValues?.type ?? BannerTypeEnum.INFO}
            />
        </>
    );
};

export default AnnouncementCreateForm;
